import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
} from '@angular/material/dialog';
import { MediaInputService } from '../../../media-input/media-input.service';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';

export interface ZoomCapabilities {
  zoom: {
    min: number;
    max: number;
  };
}

@Component({
  selector: 'app-camera-dialog',
  imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    FormsModule,
    MatIconButton,
    MatIcon,
    NgClass,
    MatSlider,
    MatSliderThumb,
  ],
  templateUrl: './camera-dialog.component.html',
  styleUrl: './camera-dialog.component.scss',
})
export class CameraDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('image') imgRef!: ElementRef<HTMLImageElement>;

  recording = false;
  stream: MediaStream | undefined;
  maxZoom: number | undefined;
  images: string[];
  getImages: () => string[];
  addImage: (image: string) => void;
  show_border = false;
  maxZoomSlider = 5;
  sliderValue = 0;

  constructor(
    private readonly mediaInputService: MediaInputService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      getImages: () => string[];
      addImage: (image: string) => void;
    },
  ) {
    this.images = data.getImages();
    this.getImages = data.getImages;
    this.addImage = data.addImage;
  }

  save() {
    const digitalZoom = this.getDigitalZoom();

    const video = this.videoRef.nativeElement;
    const sourceWidth = video.videoWidth / digitalZoom;
    const sourceHeight = video.videoHeight / digitalZoom;
    const sourceX = (video.videoWidth - sourceWidth) / 2;
    const sourceY = (video.videoHeight - sourceHeight) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    this.addImage(canvas.toDataURL('image/jpeg'));
    this.images = this.getImages();

    this.show_border = true;
    setTimeout(() => {
      this.show_border = false;
    }, 500);
  }

  applyZoom(stream: MediaStream) {
    const opticalZoom = this.getOpticalZoom();
    const digitalZoom = this.getDigitalZoom();

    //optical zoom
    if (this.maxZoom) {
      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.applyConstraints({
        advanced: [
          { zoom: opticalZoom },
        ] as unknown as MediaTrackConstraintSet[],
      });
    }

    //digital zoom
    this.videoRef.nativeElement.style.transform = `scale(${digitalZoom}`;
  }

  getTotalZoom() {
    return Math.exp(this.sliderValue);
  }

  getOpticalZoom(): number {
    if (this.maxZoom) {
      return Math.min(this.getTotalZoom(), this.maxZoom);
    }
    return 1;
  }

  getDigitalZoom(): number {
    return this.getTotalZoom() / this.getOpticalZoom();
  }

  async startRecording() {
    const stream = await this.mediaInputService.getVideoStream();
    this.videoRef.nativeElement.srcObject = stream;

    this.recording = true;
    this.stream = stream;
    const videoTrack = stream.getVideoTracks()[0];
    if ('zoom' in videoTrack.getCapabilities()) {
      this.maxZoom = (videoTrack.getCapabilities() as ZoomCapabilities)[
        'zoom'
      ].max;
      this.sliderValue = 0;
      this.applyZoom(stream);
    }
  }

  ngAfterViewInit(): void {
    this.startRecording();
  }

  ngOnDestroy(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
    }
    this.videoRef.nativeElement.srcObject = null;
  }

  protected readonly Math = Math;
}
